/**
 * ReceiptsPane — CFO-grade vendor receipt reconciliation.
 *
 * Workflow: rep creates a receipt for each vendor invoice, assigns BOM lines
 * to it, enters fees in 4 categorized buckets (freight / tax / processing /
 * other), and locks at variance = $0.00 — penny-perfect to the vendor invoice.
 *
 * Design rules:
 *  • Unit prices on lines = vendor invoice exactly. NO blended allocation.
 *  • Fees stay in their own buckets so finance can split GAAP-correctly:
 *    freight → capitalized, tax → liability, processing/other → period expense.
 *  • Locked receipts are immutable until explicitly unlocked with a reason.
 *  • Soft-gating: packet can be marked Received with unreconciled lines
 *    (warning shown), since material may arrive before the invoice.
 */
import { useMemo, useState, useEffect } from "react";
import { PermissionGate } from "@/components/PermissionGate";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Receipt as ReceiptIcon, Plus, Lock, Unlock, Trash2, AlertTriangle,
  CheckCircle2, FileText,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { useOrderingPacket } from "@/data/orderingPacket/context";
import {
  type OrderingPacket, type VendorReceipt, type ReceiptFeeBucket,
  RECEIPT_FEE_BUCKET_LABEL, RECEIPT_FEE_BUCKET_HINT,
} from "@/data/orderingPacket/types";
import { format } from "date-fns";

interface ReceiptsPaneProps {
  packet: OrderingPacket;
}

const FEE_BUCKETS: ReceiptFeeBucket[] = ["freight", "tax", "processing", "other"];

export function ReceiptsPane({ packet }: ReceiptsPaneProps) {
  const {
    createReceipt, updateReceipt, assignLineToReceipt,
    lockReceipt, unlockReceipt, deleteReceipt,
  } = useOrderingPacket();

  const receipts = packet.receipts ?? [];
  const unreconciledLines = packet.lineItems.filter((l) => !l.receiptId);
  const reconciledCount = packet.lineItems.length - unreconciledLines.length;

  const [expandedReceiptId, setExpandedReceiptId] = useState<string | null>(
    receipts.find((r) => !r.locked)?.id ?? receipts[0]?.id ?? null,
  );

  const handleAdd = () => {
    const r = createReceipt(packet.id, {
      vendorName: packet.intendedVendor,
    });
    if (r) setExpandedReceiptId(r.id);
  };

  return (
    <div className="h-full min-h-0 overflow-hidden grid grid-rows-[auto_minmax(0,1fr)]">
      {/* Top summary bar */}
      <div className={cn(
        "flex items-center gap-3 px-3 py-2 mb-3 rounded-md border text-[11px] font-medium shrink-0",
        unreconciledLines.length === 0 && receipts.length > 0
          ? "bg-success/10 border-success/30 text-success"
          : "bg-primary/10 border-primary/30",
      )}>
        <ReceiptIcon className="h-3.5 w-3.5 shrink-0" />
        <span className="font-semibold">
          {receipts.length === 0
            ? "No receipts yet — create one to start reconciling"
            : unreconciledLines.length === 0
              ? `All ${packet.lineItems.length} lines reconciled across ${receipts.length} receipt${receipts.length === 1 ? "" : "s"}`
              : `${reconciledCount} of ${packet.lineItems.length} lines reconciled · ${unreconciledLines.length} unassigned`}
        </span>
        <div className="ml-auto">
          <PermissionGate permission="ordering_approve">
            <Button size="sm" className="h-7 text-[11px] gap-1.5" onClick={handleAdd}>
              <Plus className="h-3 w-3" /> Add Receipt
            </Button>
          </PermissionGate>
        </div>
      </div>

      {/* Scrollable receipt list */}
      <div className="min-h-0 overflow-y-auto overscroll-contain space-y-3 pr-1">
        {receipts.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-48 text-center border border-dashed border-border rounded-lg">
            <FileText className="h-8 w-8 text-muted-foreground/40 mb-2" />
            <p className="text-[12px] font-semibold text-muted-foreground">No vendor receipts yet</p>
            <p className="text-[10px] text-muted-foreground mt-1 max-w-xs">
              Receipts let you reconcile each vendor invoice penny-for-penny — line items, fees, and tax all match the invoice total.
            </p>
            <PermissionGate permission="ordering_approve">
              <Button size="sm" className="h-7 text-[11px] gap-1.5 mt-3" onClick={handleAdd}>
                <Plus className="h-3 w-3" /> Create first receipt
              </Button>
            </PermissionGate>
          </div>
        ) : (
          receipts.map((r) => (
            <ReceiptCard
              key={r.id}
              receipt={r}
              packet={packet}
              expanded={expandedReceiptId === r.id}
              onToggleExpand={() => setExpandedReceiptId(expandedReceiptId === r.id ? null : r.id)}
              onUpdate={(patch) => updateReceipt(packet.id, r.id, patch)}
              onAssignLine={(lineId, on) => assignLineToReceipt(packet.id, lineId, on ? r.id : null)}
              onLock={() => lockReceipt(packet.id, r.id)}
              onUnlock={(reason) => unlockReceipt(packet.id, r.id, reason)}
              onDelete={() => deleteReceipt(packet.id, r.id)}
            />
          ))
        )}
      </div>
    </div>
  );
}

/* ────────── RECEIPT CARD ────────── */
function ReceiptCard({
  receipt, packet, expanded, onToggleExpand, onUpdate, onAssignLine, onLock, onUnlock, onDelete,
}: {
  receipt: VendorReceipt;
  packet: OrderingPacket;
  expanded: boolean;
  onToggleExpand: () => void;
  onUpdate: (patch: Partial<Omit<VendorReceipt, "id" | "packetId" | "createdAt" | "createdBy" | "locked" | "lockedAt" | "lockedBy">>) => void;
  onAssignLine: (lineId: string, on: boolean) => void;
  onLock: () => void;
  onUnlock: (reason: string) => void;
  onDelete: () => void;
}) {
  const [unlockReason, setUnlockReason] = useState("");
  const [showUnlock, setShowUnlock] = useState(false);

  const assignedLines = useMemo(
    () => packet.lineItems.filter((l) => l.receiptId === receipt.id),
    [packet.lineItems, receipt.id],
  );
  const lineSubtotal = useMemo(
    () => assignedLines.reduce(
      (s, l) => s + (l.actualUnitPrice ?? l.projectedUnitPrice) * (l.actualQty ?? l.projectedQty),
      0,
    ),
    [assignedLines],
  );
  const feesTotal = receipt.fees.freight + receipt.fees.tax + receipt.fees.processing + receipt.fees.other;
  const calculatedTotal = lineSubtotal + feesTotal;
  const variance = +(calculatedTotal - receipt.invoiceTotal).toFixed(2);
  const variancePennies = Math.round(variance * 100);
  const isReconciled = receipt.invoiceTotal > 0 && variancePennies === 0;
  const otherNoteRequired = receipt.fees.other > 0 && !receipt.otherFeeNote?.trim();
  const canLock = isReconciled && assignedLines.length > 0 && !otherNoteRequired && receipt.invoiceNumber.trim().length > 0;

  const isLocked = receipt.locked;
  const unassignedLines = packet.lineItems.filter((l) => !l.receiptId);

  return (
    <div className={cn(
      "rounded-lg border transition-colors",
      isLocked
        ? "border-success/40 bg-success/[0.04]"
        : isReconciled
          ? "border-primary/40 bg-primary/[0.04]"
          : "border-border bg-card",
    )}>
      {/* Header row — always visible */}
      <button
        type="button"
        onClick={onToggleExpand}
        className="w-full flex items-center gap-3 p-3 text-left hover:bg-muted/30 transition-colors rounded-t-lg"
      >
        {isLocked ? (
          <Lock className="h-4 w-4 text-success shrink-0" />
        ) : (
          <ReceiptIcon className="h-4 w-4 text-muted-foreground shrink-0" />
        )}
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-[12px] font-semibold">
              {receipt.vendorName || "Vendor"}
              {receipt.invoiceNumber && (
                <span className="text-muted-foreground font-normal"> · #{receipt.invoiceNumber}</span>
              )}
            </span>
            {receipt.invoiceDate && (
              <span className="text-[10px] text-muted-foreground">
                {format(new Date(receipt.invoiceDate), "MMM d, yyyy")}
              </span>
            )}
            <span className="text-[10px] text-muted-foreground">
              · {assignedLines.length} line{assignedLines.length === 1 ? "" : "s"}
            </span>
          </div>
          <div className="text-[10px] text-muted-foreground mt-0.5">
            Created {format(new Date(receipt.createdAt), "MMM d, h:mm a")} by {receipt.createdBy}
            {isLocked && receipt.lockedAt && ` · Locked ${format(new Date(receipt.lockedAt), "MMM d, h:mm a")} by ${receipt.lockedBy}`}
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <span className="text-[11px] tabular-nums text-muted-foreground">
            ${calculatedTotal.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} of ${receipt.invoiceTotal.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
          </span>
          <VariancePill variance={variance} isLocked={isLocked} />
        </div>
      </button>

      {/* Expanded body */}
      {expanded && (
        <div className="p-4 pt-0 border-t border-border/50 space-y-4">
          {/* Invoice metadata */}
          <div className="grid grid-cols-12 gap-3 pt-3">
            <FieldLabel label="Vendor" className="col-span-3">
              <Input
                value={receipt.vendorName}
                onChange={(e) => onUpdate({ vendorName: e.target.value })}
                disabled={isLocked}
                className="h-8 text-[11px]"
              />
            </FieldLabel>
            <FieldLabel label="Invoice #" className="col-span-3">
              <Input
                value={receipt.invoiceNumber}
                onChange={(e) => onUpdate({ invoiceNumber: e.target.value })}
                placeholder="e.g. INV-7782"
                disabled={isLocked}
                className="h-8 text-[11px]"
              />
            </FieldLabel>
            <FieldLabel label="Invoice Date" className="col-span-3">
              <Input
                type="date"
                value={receipt.invoiceDate.slice(0, 10)}
                onChange={(e) => onUpdate({ invoiceDate: e.target.value })}
                disabled={isLocked}
                className="h-8 text-[11px]"
              />
            </FieldLabel>
            <FieldLabel label="Invoice Total" className="col-span-3" hint="Amount printed on the invoice">
              <CurrencyInput
                value={receipt.invoiceTotal}
                onChange={(n) => onUpdate({ invoiceTotal: n })}
                disabled={isLocked}
                placeholder="0.00"
              />
            </FieldLabel>
          </div>

          {/* Line assignment */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <h4 className="text-[11px] font-bold uppercase tracking-wider text-foreground">
                Line Items on This Receipt
              </h4>
              <span className="text-[10px] text-muted-foreground tabular-nums">
                Subtotal: ${lineSubtotal.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </span>
            </div>
            <div className="rounded-md border border-border divide-y divide-border max-h-64 overflow-y-auto">
              {/* Already assigned to this receipt */}
              {assignedLines.map((l) => {
                const lineCost = (l.actualUnitPrice ?? l.projectedUnitPrice) * (l.actualQty ?? l.projectedQty);
                return (
                  <div key={l.id} className="flex items-center gap-2 px-2 py-1.5 text-[11px] bg-success/[0.05]">
                    <Checkbox
                      checked
                      disabled={isLocked}
                      onCheckedChange={(c) => !c && onAssignLine(l.id, false)}
                      className="h-3.5 w-3.5"
                    />
                    <span className="font-mono text-[10px] text-muted-foreground w-[110px] truncate">{l.actualSku ?? l.projectedSku}</span>
                    <span className="flex-1 truncate">{l.actualItemName ?? l.projectedItemName}</span>
                    <span className="text-muted-foreground tabular-nums">{l.actualQty ?? l.projectedQty} ×</span>
                    <span className="tabular-nums w-20 text-right">
                      ${(l.actualUnitPrice ?? l.projectedUnitPrice).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                    </span>
                    <span className="tabular-nums w-24 text-right font-semibold">
                      ${lineCost.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                    </span>
                  </div>
                );
              })}
              {/* Unassigned lines (only show when not locked) */}
              {!isLocked && unassignedLines.length > 0 && (
                <>
                  <div className="px-2 py-1 text-[9px] uppercase tracking-wider font-semibold text-muted-foreground bg-muted/30">
                    Unassigned — check to add
                  </div>
                  {unassignedLines.map((l) => {
                    const lineCost = (l.actualUnitPrice ?? l.projectedUnitPrice) * (l.actualQty ?? l.projectedQty);
                    return (
                      <div key={l.id} className="flex items-center gap-2 px-2 py-1.5 text-[11px] hover:bg-muted/30">
                        <Checkbox
                          checked={false}
                          onCheckedChange={(c) => c && onAssignLine(l.id, true)}
                          className="h-3.5 w-3.5"
                        />
                        <span className="font-mono text-[10px] text-muted-foreground w-[110px] truncate">{l.actualSku ?? l.projectedSku}</span>
                        <span className="flex-1 truncate text-muted-foreground">{l.actualItemName ?? l.projectedItemName}</span>
                        <span className="text-muted-foreground tabular-nums">{l.actualQty ?? l.projectedQty} ×</span>
                        <span className="tabular-nums w-20 text-right text-muted-foreground">
                          ${(l.actualUnitPrice ?? l.projectedUnitPrice).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                        </span>
                        <span className="tabular-nums w-24 text-right text-muted-foreground">
                          ${lineCost.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                        </span>
                      </div>
                    );
                  })}
                </>
              )}
              {assignedLines.length === 0 && unassignedLines.length === 0 && (
                <div className="px-3 py-3 text-[10px] text-muted-foreground text-center">
                  No lines available.
                </div>
              )}
            </div>
          </div>

          {/* Fee buckets — categorized */}
          <div>
            <h4 className="text-[11px] font-bold uppercase tracking-wider text-foreground mb-2">
              Charges (categorized)
            </h4>
            <div className="grid grid-cols-2 gap-3">
              {FEE_BUCKETS.map((bucket) => (
                <div key={bucket} className="space-y-1">
                  <label className="text-[10px] font-semibold text-foreground">
                    {RECEIPT_FEE_BUCKET_LABEL[bucket]}
                  </label>
                  <CurrencyInput
                    value={receipt.fees[bucket]}
                    onChange={(n) => onUpdate({ fees: { ...receipt.fees, [bucket]: n } })}
                    disabled={isLocked}
                    placeholder="0.00"
                  />
                  <p className="text-[9px] text-muted-foreground">{RECEIPT_FEE_BUCKET_HINT[bucket]}</p>
                </div>
              ))}
            </div>
            {receipt.fees.other > 0 && (
              <div className="mt-2">
                <label className="text-[10px] font-semibold text-foreground">
                  Note for "Other" charge <span className="text-destructive">*</span>
                </label>
                <Input
                  value={receipt.otherFeeNote ?? ""}
                  onChange={(e) => onUpdate({ otherFeeNote: e.target.value })}
                  placeholder="What is this charge for?"
                  disabled={isLocked}
                  className={cn("h-8 text-[11px] mt-1", otherNoteRequired && "border-warning bg-warning/5")}
                />
              </div>
            )}
          </div>

          {/* Totals strip */}
          <div className="rounded-md border border-border bg-muted/30 p-3 grid grid-cols-5 gap-3 text-[11px]">
            <Total label="Lines" value={lineSubtotal} />
            <Total label="Freight" value={receipt.fees.freight} />
            <Total label="Tax" value={receipt.fees.tax} />
            <Total label="Fees" value={receipt.fees.processing + receipt.fees.other} />
            <Total label="Receipt Total" value={calculatedTotal} bold />
          </div>

          {/* Variance + reconcile bar */}
          <div className={cn(
            "rounded-md p-3 flex items-center gap-3",
            isReconciled ? "bg-success/10 border border-success/30" : "bg-warning/10 border border-warning/30",
          )}>
            {isReconciled ? (
              <CheckCircle2 className="h-4 w-4 text-success shrink-0" />
            ) : (
              <AlertTriangle className="h-4 w-4 text-warning shrink-0" />
            )}
            <div className="flex-1">
              <div className="text-[11px] font-semibold">
                {isReconciled
                  ? "Variance: $0.00 — penny-perfect"
                  : `Variance: ${variance > 0 ? "+" : ""}$${Math.abs(variance).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
              </div>
              <div className="text-[10px] text-muted-foreground">
                {isReconciled
                  ? "Calculated total matches the invoice total. Ready to lock."
                  : variance > 0
                    ? "Calculated total is higher than invoice — reduce a fee or unit price."
                    : "Calculated total is lower than invoice — likely a missing tax/freight charge."}
              </div>
            </div>
            {!isLocked ? (
              <PermissionGate permission="ordering_approve">
                <Button
                  size="sm"
                  className="h-8 text-[11px] gap-1.5 bg-success hover:bg-success/90 text-success-foreground"
                  disabled={!canLock}
                  onClick={onLock}
                  title={!canLock ? "Variance must be $0.00, invoice # required, all 'Other' charges noted" : "Lock receipt"}
                >
                  <Lock className="h-3 w-3" /> Lock Receipt
                </Button>
              </PermissionGate>
            ) : showUnlock ? (
              <div className="flex items-center gap-2">
                <Input
                  value={unlockReason}
                  onChange={(e) => setUnlockReason(e.target.value)}
                  placeholder="Reason for unlock…"
                  className="h-8 text-[11px] w-48"
                />
                <Button
                  size="sm" variant="outline" className="h-8 text-[11px]"
                  onClick={() => { setShowUnlock(false); setUnlockReason(""); }}
                >
                  Cancel
                </Button>
                <PermissionGate permission="ordering_approve">
                  <Button
                    size="sm" className="h-8 text-[11px]"
                    disabled={!unlockReason.trim()}
                    onClick={() => { onUnlock(unlockReason.trim()); setShowUnlock(false); setUnlockReason(""); }}
                  >
                    Confirm Unlock
                  </Button>
                </PermissionGate>
              </div>
            ) : (
              <PermissionGate permission="ordering_approve">
                <Button
                  size="sm" variant="outline" className="h-8 text-[11px] gap-1.5"
                  onClick={() => setShowUnlock(true)}
                >
                  <Unlock className="h-3 w-3" /> Unlock
                </Button>
              </PermissionGate>
            )}
          </div>

          {/* Notes + delete */}
          <div className="grid grid-cols-12 gap-3">
            <FieldLabel label="Notes" className="col-span-10">
              <Textarea
                value={receipt.notes ?? ""}
                onChange={(e) => onUpdate({ notes: e.target.value })}
                disabled={isLocked}
                placeholder="Optional notes about this receipt…"
                className="text-[11px] min-h-[60px]"
              />
            </FieldLabel>
            <div className="col-span-2 flex items-end justify-end">
              {!isLocked && (
                <PermissionGate permission="ordering_approve">
                  <Button
                    size="sm" variant="ghost"
                    className="h-8 text-[11px] text-destructive hover:bg-destructive/10"
                    onClick={onDelete}
                  >
                    <Trash2 className="h-3 w-3 mr-1" /> Delete
                  </Button>
                </PermissionGate>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* ────────── PRIMITIVES ────────── */
function VariancePill({ variance, isLocked }: { variance: number; isLocked: boolean }) {
  const isZero = Math.round(variance * 100) === 0;
  if (isLocked) {
    return (
      <Badge className="text-[9px] font-semibold border bg-success/15 text-success border-success/30">
        Locked
      </Badge>
    );
  }
  if (isZero) {
    return (
      <Badge className="text-[9px] font-semibold border bg-primary/15 text-primary border-primary/30">
        $0.00 ready
      </Badge>
    );
  }
  return (
    <Badge className="text-[9px] font-semibold border bg-warning/15 text-warning border-warning/30 tabular-nums">
      {variance > 0 ? "+" : "−"}${Math.abs(variance).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
    </Badge>
  );
}

function FieldLabel({ label, hint, children, className }: { label: string; hint?: string; children: React.ReactNode; className?: string }) {
  return (
    <div className={cn("space-y-1", className)}>
      <label className="text-[9px] font-semibold uppercase tracking-wider text-muted-foreground">
        {label}{hint && <span className="ml-1 normal-case tracking-normal text-muted-foreground/70 font-normal">— {hint}</span>}
      </label>
      {children}
    </div>
  );
}

function Total({ label, value, bold }: { label: string; value: number; bold?: boolean }) {
  return (
    <div>
      <div className="text-[9px] uppercase tracking-wider text-muted-foreground font-semibold">{label}</div>
      <div className={cn("tabular-nums", bold ? "text-[13px] font-bold" : "text-[12px] font-semibold")}>
        ${value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
      </div>
    </div>
  );
}

function CurrencyInput({
  value, onChange, disabled, placeholder,
}: { value: number; onChange: (n: number) => void; disabled?: boolean; placeholder?: string }) {
  const [draft, setDraft] = useState<string>(value === 0 ? "" : value.toFixed(2));

  useMemoSync(value, () => setDraft(value === 0 ? "" : value.toFixed(2)));

  return (
    <Input
      type="text"
      inputMode="decimal"
      value={draft}
      placeholder={placeholder}
      disabled={disabled}
      onChange={(e) => {
        const v = e.target.value;
        if (v === "" || /^\d*\.?\d{0,2}$/.test(v)) setDraft(v);
      }}
      onBlur={() => {
        const n = parseFloat(draft);
        const next = isNaN(n) ? 0 : n;
        onChange(next);
        setDraft(next === 0 ? "" : next.toFixed(2));
      }}
      className="h-8 text-[11px] text-right tabular-nums [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
    />
  );
}

function useMemoSync<T>(dep: T, fn: () => void) {
  useEffect(() => { fn();   }, [dep]);
}
